import { type ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"

/** A person picked from the directory. ``email`` is absent outside mail flows. */
export type PickedPerson = {
  oid: string
  displayName: string
  email?: string
}

function toPickedPerson(result: DirectorySearchResult): PickedPerson {
  return { oid: result.oid, displayName: result.displayName, email: result.email ?? "" }
}

/**
 * One selected person, with a remove button.
 *
 * ``requireEmail`` marks a missing address as an error rather than hiding it —
 * only the hand-over flow needs that, since it cannot send without one.
 */
export function PersonRow({
  person,
  icon,
  onRemove,
  requireEmail = false,
  disabled = false,
}: {
  person: PickedPerson
  icon?: ReactNode
  onRemove: () => void
  requireEmail?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation()
  let subline = null
  if (person.email) {
    subline = <p className="text-muted-foreground truncate text-xs">{person.email}</p>
  } else if (requireEmail) {
    subline = <p className="text-destructive truncate text-xs">{t("tender.people.noEmail")}</p>
  }
  return (
    <div className="bg-muted/40 flex items-center justify-between gap-2 rounded-md px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        {icon}
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{person.displayName}</p>
          {subline}
        </div>
      </div>
      <Button
        aria-label={t("tender.people.remove")}
        className="size-7 shrink-0"
        disabled={disabled}
        size="icon"
        variant="ghost"
        onClick={onRemove}
      >
        <X className="size-4" />
      </Button>
    </div>
  )
}

/**
 * Directory picker for one person (``multiple={false}``) or several.
 *
 * Labels are passed in already translated so this stays usable from creation,
 * Setup and hand-over without collecting their copy into one namespace.
 * ``scopeGroup`` is enforced server-side in routes/graph.py — passing the
 * tender hand-over group here needs no backend change.
 *
 * ``dimmedOids`` reach DirectorySearch as ``selectedOids``, which only changes
 * how a result is styled — it does NOT remove it from the list. Named for what
 * it does rather than what it looks like it does.
 */
export function PersonPicker({
  label,
  hint,
  placeholder,
  selected,
  onChange,
  multiple = false,
  icon,
  scopeGroup,
  disabled = false,
  requireEmail = false,
  dimmedOids = [],
}: {
  label: string
  hint?: string
  placeholder: string
  selected: PickedPerson[]
  onChange: (next: PickedPerson[]) => void
  multiple?: boolean
  icon?: ReactNode
  scopeGroup?: string
  disabled?: boolean
  requireEmail?: boolean
  dimmedOids?: string[]
}) {
  const handleSelect = (result: DirectorySearchResult) => {
    const person = toPickedPerson(result)
    if (!multiple) {
      onChange([person])
      return
    }
    if (selected.some((p) => p.oid === person.oid)) return
    onChange([...selected, person])
  }

  // A single-select picker hides its input once filled — the way to change the
  // pick is to remove it, which keeps "who is Ansvarig" unambiguous.
  const showInput = multiple || selected.length === 0

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      {selected.map((person) => (
        <PersonRow
          key={person.oid}
          disabled={disabled}
          icon={icon}
          person={person}
          requireEmail={requireEmail}
          onRemove={() => onChange(selected.filter((p) => p.oid !== person.oid))}
        />
      ))}
      {showInput ? (
        <DirectorySearch
          // Tender pickers (Ansvarig, Deltagare) never offer guests.
          membersOnly
          disabled={disabled}
          placeholder={placeholder}
          scopeGroup={scopeGroup}
          selectedOids={[...selected.map((p) => p.oid), ...dimmedOids]}
          types={["users"]}
          onSelect={handleSelect}
        />
      ) : null}
    </div>
  )
}
